import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import { checkValidId, INITIAL_COMPOSITIONS, UPDATE_ENTITY_TYPE } from '@components/rangePlan/rangePlanUtils';

import useClickOutside from '@hooks/useClickOutside';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { getFabricCompositions } from '@services/commonService';

import { printApiError } from '@utils/Util';

const CompositionInput = ({
	compositionsList,
	selectedFiber,
	onChangePercentage,
	percentage,
	onSelectFiber
}) => {
	return (
		<div className="d-flex composition-input">
			<input
				type="number"
				placeholder="%"
				min={0.1}
				max={100}
				className="input-percentage"
				onChange={(e) => onChangePercentage(e)}
				value={percentage}
			/>
			<div className="select-component">
				<div className="form-group size36">
					<CustomDropdown
						items={compositionsList}
						selectedItem={selectedFiber}
						onItemClick={(value) => {
							onSelectFiber(value);
						}}
						toolTipLength={8}
					/>
				</div>
			</div>
		</div>
	);
};

const CompositionPopup = ({ item, onUpdateTarget, id, entityType, handleClosePopup }) => {
	const [compositionsList, setCompositionsList] = useState([]);
	const [selectedComposition, setSelectedComposition] = useState(INITIAL_COMPOSITIONS);
	const dispatch = useDispatch();
	const [errors, setErrors] = useState({});
	const compositionRef = useRef(null);

	useEffect(() => {
		const compositions = item?.targetFabricCompositions;
		const formattedCompositions = compositions?.reduce((acc, composition, index) => {
			acc[`composition_${index + 1}`] = composition?.map((fiber) => ({
				id: fiber?.id,
				percentage: fiber?.percentage,
				fiberName: fiber?.fiberName,
				fiberDetails: fiber?.fiberDetails || {}
			}));
			return acc;
		}, {});

		if (compositions?.length > 0) {
			setSelectedComposition(formattedCompositions);
		}
	}, [item?.targetFabricCompositions?.length]);

	const fetchCompositions = async () => {
		try {
			dispatch(openLoader());
			const response = await getFabricCompositions();
			const updatedResponse = response?.map((item) => ({
				...item,
				value: item
			}));

			setCompositionsList(updatedResponse);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		fetchCompositions();
	}, []);

	const getTotalPercentage = (compositionRows) => {
		const totalPercentage = compositionRows?.reduce(
			(acc, curr) => acc + (parseInt(curr.percentage) || 0),
			0
		);
		return totalPercentage;
	};

	const isAllRowsHaveValues = (compositionRows) => {
		return compositionRows.every((row) => row.fiberName && row.percentage);
	};

	const compositionRows = (compositionKey) => selectedComposition[compositionKey];

	const validateCompositions = () => {
		const newErrors = {};
		Object.keys(selectedComposition)?.forEach((compositionKey) => {
			if (
				!isAllRowsHaveValues(compositionRows(compositionKey)) &&
				getTotalPercentage(compositionRows(compositionKey)) !== 100
			) {
				newErrors[compositionKey] = 'Please create composition';
			} else if (getTotalPercentage(compositionRows(compositionKey)) !== 100) {
				newErrors[compositionKey] = `Total percentage must be 100%, but it's ${getTotalPercentage(
					compositionRows(compositionKey)
				)}%`;
			}
		});

		setErrors(newErrors);
		return newErrors;
	};

	const checkCompositionPercentage = () => {
		const allExistingIds = Object.values(selectedComposition)
			.flat()
			.map((row) => row.id);
		const maxId = Math.max(...allExistingIds, 0);

		Object.keys(selectedComposition)?.forEach((compositionKey) => {
			if (
				getTotalPercentage(compositionRows(compositionKey)) < 100 &&
				isAllRowsHaveValues(compositionRows(compositionKey))
			) {
				const newRow = {
					fiberName: '',
					fiberDetails: {},
					percentage: '',
					id: maxId + 1
				};

				setSelectedComposition((prev) => ({
					...prev,
					[compositionKey]: [...prev[compositionKey], newRow]
				}));
			}
		});
	};

	const handleUpdateComposition = (e, selectedRow, rowId, selectedFiber) => {
		const updateCurrentRow = selectedComposition[selectedRow]?.map((item) => {
			if (item?.id === rowId) {
				return {
					...item,
					percentage: e ? parseInt(e?.target?.value) : item?.percentage,
					fiberDetails: selectedFiber || item?.fiberDetails,
					fiberName: selectedFiber?.value || item?.fiberName
				};
			} else return item;
		});

		setSelectedComposition((prev) => ({
			...prev,
			[selectedRow]: updateCurrentRow
		}));
	};

	const addNewCompositionRow = () => {
		const newKeyNumber = Object.keys(selectedComposition)?.length + 1;
		const newKey = `composition_${newKeyNumber}`;

		const newComposition = [
			{
				fiberName: '',
				fiberDetails: {},
				percentage: '',
				id: newKeyNumber + 1
			},
			{
				fiberName: '',
				fiberDetails: {},
				percentage: '',
				id: newKeyNumber + 2
			}
		];

		setSelectedComposition((prev) => ({
			...prev,
			[newKey]: newComposition
		}));
	};

	const handleRemoveRow = (key) => {
		const updatedCompositions = { ...selectedComposition };
		delete updatedCompositions[key];
		setSelectedComposition(updatedCompositions);
	};

	const resetCompositions = () => {
		setErrors({});
		setSelectedComposition(INITIAL_COMPOSITIONS);
	};

	const getCompositions = () => {
		return Object.keys(selectedComposition)?.map((key) => {
			const compositions = selectedComposition[key];
			return compositions
				.map((comp) => {
					if (!comp.percentage && !comp.fiberName) {
						return '';
					}
					if (!comp.percentage) {
						return comp.fiberName;
					}
					if (!comp.fiberName) {
						return '';
					}
					return `${comp.percentage}% ${comp.fiberName}`;
				})
				.filter(Boolean)
				.join(' ');
		});
	};

	const handleSubmit = () => {
		if (Object.keys(validateCompositions()).length > 0) {
			return;
		}
		const body = {
			id: checkValidId(item),
			entityId: id,
			entityType: entityType,
			productGroupId: item?.productGroupResponse?.id,
			categoryId: item?.categoryResponse?.id,
			fabricType: item?.fabricType?.constant,
			targetCompositions: getCompositions(),
			fieldName: UPDATE_ENTITY_TYPE.COMPOSITION
		};
		const handleReset = () => {
			resetCompositions();
			handleClosePopup();
		};
		onUpdateTarget(body, handleReset);
	};

	// To create new row if composition not 100%
	const handleOutsideClick = () => {
		checkCompositionPercentage();
	};

	useClickOutside(compositionRef, handleOutsideClick);

	return (
		<div className="composition-box shadow-8dp p-3">
			{Object.keys(selectedComposition).map((compositionKey, index) => (
				<React.Fragment key={compositionKey}>
					<div className="row">
						<label className="ml-3" style={{ zIndex: '999' }}>
							Composition {index + 1}
							{index !== 0 && (
								<CommonImage
									src="/icons/trash.svg"
									width={20}
									alt="delete-icon"
									className="ml-2 cursor-pointer"
									onClick={() => handleRemoveRow(compositionKey)}
								/>
							)}
						</label>
						{selectedComposition[compositionKey].map((item) => (
							<div className="col-md-6 pr-1" key={item.id}>
								<div className="form-group composition-item mb-0">
									<CompositionInput
										compositionsList={compositionsList}
										selectedFiber={item.fiberName}
										percentage={item.percentage}
										onChangePercentage={(e) =>
											handleUpdateComposition(e, compositionKey, item.id)
										}
										onSelectFiber={(selectedFiber) =>
											handleUpdateComposition(
												null,
												compositionKey,
												item.id,
												selectedFiber
											)
										}
										ref={compositionRef}
									/>
								</div>
							</div>
						))}
					</div>
					<p className="red" style={{ marginTop: '-12px', fontSize: '13px' }}>
						{errors[compositionKey]}
					</p>
				</React.Fragment>
			))}
			<div className="add-btn">
				<button className="button text pl-0" onClick={addNewCompositionRow}>
					<span>Add</span>
					<CommonImage src="/icons/plus-20.svg" alt="add-icon" isStatic={true} />
				</button>
			</div>
			<div className="footer-buttons text-right">
				<button
					ref={compositionRef}
					className="button text size32 p-0 mr-1"
					onClick={(e) => {
						e.stopPropagation();
						handleClosePopup();
					}}
				>
					CANCEL
				</button>
				<button className="button text size32 p-0" onClick={handleSubmit}>
					SUBMIT
				</button>
			</div>
		</div>
	);
};

export default CompositionPopup;
